import React, { ReactElement, useState, useCallback } from 'react';
import { MenuItem, ListItemIcon, ListItemText, SelectProps } from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { StandardInputWrapper } from '@components/StatefulInputs/Base/StandardInputWrapper/StandardInputWrapper';
import { FieldValues, useController } from 'react-hook-form';
import { makeStyles } from '@material-ui/core/styles';
import { StatefulMultiSelectProps } from '@components/StatefulInputs/StatefulMultiSelect/StatefulMultiSelectProps';
import { Theme as DefaultTheme } from '@material-ui/core/styles/createTheme';
import {
	isOptionSelected,
	renderSelectedValues
} from '@components/StatefulInputs/StatefulMultiSelect/statefulMultiSelectUtilities';
import { SelectOption } from '@components/StatefulInputs/StatefulSelect/StatefulSelect.types';
import { ChatAwareSelect } from '@components/ChatAwareComponents/ChatAwareSelect/ChatAwareSelect';

const useStyles = makeStyles<DefaultTheme, Record<'columns', number | undefined>>((theme) => ({
	menu: ({ columns }) => ({
		'& .MuiPaper-root .MuiList-root': {
			columns: columns,
			padding: theme.spacing(2),
		},
		'& .MuiPaper-root.MuiMenu-paper': {
			width: '40%',
		},
		'& li.MuiMenuItem-root': {
			'textWrap': 'wrap',
			'alignItems': 'center',
			'& .MuiListItemIcon-root': {
				minWidth: 'unset',
				marginRight: theme.spacing(1),
			},
			'paddingTop': 0,
			'paddingBottom': theme.spacing(1),
		},
		'& .MuiListItem-root.Mui-selected': {
			backgroundColor: 'unset'
		}
	}),
	formControlLabel: {
		'& .MuiTypography-root': {
			fontWeight: 400,
		}
	},
	placeholder: {
		color: theme.palette.grey[600],
	},
}));

export function StatefulMultiSelect<FormType extends FieldValues>({
	options,
	controllerOptions,
	columns,
	placeholder,
	...statefulProviderProps
}: StatefulMultiSelectProps<FormType> & Pick<SelectProps, 'placeholder'>): ReactElement {
	const classes = useStyles({ columns });
	const { field } = useController<FormType>(
		{ name: statefulProviderProps.name, ...controllerOptions }
	);
	const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>(field.value || []);

	const handleOptionChange = useCallback((option: SelectOption) => {
		setSelectedOptions((prevSelected) => {
			const isSelected = isOptionSelected(prevSelected, option);
			if (isSelected) {
				return prevSelected.filter((item) => item.value !== option.value);
			} else {
				return [...prevSelected, option];
			}
		});
	}, []);

	const handleBlur = useCallback(() => {
		field.onChange(selectedOptions);
	}, [field, selectedOptions]);

	const handleEnterKeyToSelect = useCallback((event: React.KeyboardEvent<HTMLLIElement>, option: SelectOption) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			handleOptionChange(option);
		}
	}, [handleOptionChange]);

	return (
		<StandardInputWrapper<FormType>
			{...statefulProviderProps}
			labelProps={{
				id: `multiselect-label-${statefulProviderProps.name}`,
				htmlFor: `multiselect-field-${statefulProviderProps.name}`,
			}}
		>
			<ChatAwareSelect
				{...field}
				name={statefulProviderProps.name}
				id={`multiselect-field-${statefulProviderProps.name}`}
				labelId={`multiselect-label-${statefulProviderProps.name}`}
				value={field.value || controllerOptions.defaultValue || ''}
				multiple
				MenuProps={{
					className: classes.menu,
					// disableAutoFocusItem & getContentAnchorEl are required
					// to prevent the menu from auto scrolling on option click
					disableAutoFocusItem: true,
					getContentAnchorEl: null,
				}}
				displayEmpty={!!placeholder}
				renderValue={() => {
					if (!field.value?.length && placeholder) {
						return <span className={classes.placeholder}>{placeholder}</span>;
					}
					return renderSelectedValues(field.value);
				}}
				onBlurCapture={handleBlur}
			>
				{options.map((option) => {
					const selected = isOptionSelected(selectedOptions, option);
					return (
						<MenuItem
							key={option.value}
							value={option.value}
							aria-selected={selected}
							onClick={() => handleOptionChange(option)}
							onKeyDown={(event) => handleEnterKeyToSelect(event, option)}
							data-field={`multiselect-option-${statefulProviderProps.name}`}
						>
							<ListItemIcon>
								{selected ? <CheckBoxIcon color="primary" /> : <CheckBoxOutlineBlankIcon />}
							</ListItemIcon>
							<ListItemText primary={option.label} />
						</MenuItem>
					);
				})}
			</ChatAwareSelect>
		</StandardInputWrapper>
	);
}
